import dayjs, { Dayjs } from "dayjs";
import { CSSProperties, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarEvent,
  ScheduleSummary,
} from "../../services/scheduling/Scheduling.types";
import {
  assignEventLanes,
  CALENDAR_END_HOUR,
  CALENDAR_HOUR_HEIGHT,
  CALENDAR_START_HOUR,
  eventTimeLabel,
  eventsForDay,
  getScheduleColor,
} from "./calendar.utils";
import styles from "./ScheduleCalendar.module.css";

interface TimeGridProps {
  days: Dayjs[];
  events: CalendarEvent[];
  schedules: ScheduleSummary[];
  onEventClick: (event: CalendarEvent) => void;
}

interface EventStyle extends CSSProperties {
  "--event-color": string;
}

const hourCount = CALENDAR_END_HOUR - CALENDAR_START_HOUR;
const gridHeight = hourCount * CALENDAR_HOUR_HEIGHT;

function eventPosition(event: CalendarEvent, day: Dayjs) {
  const dayStart = day.startOf("day");
  const visibleStart = dayStart.add(CALENDAR_START_HOUR, "hour");
  const visibleEnd = dayStart.add(CALENDAR_END_HOUR, "hour");
  const start = dayjs(event.start).isBefore(visibleStart)
    ? visibleStart
    : dayjs(event.start);
  const end = dayjs(event.end).isAfter(visibleEnd)
    ? visibleEnd
    : dayjs(event.end);
  const topMinutes = start.diff(visibleStart, "minute", true);
  const durationMinutes = Math.max(20, end.diff(start, "minute", true));

  return {
    top: (topMinutes / 60) * CALENDAR_HOUR_HEIGHT,
    height: Math.max(28, (durationMinutes / 60) * CALENDAR_HOUR_HEIGHT),
  };
}

export default function TimeGrid({
  days,
  events,
  schedules,
  onEventClick,
}: TimeGridProps) {
  const { t } = useTranslation();
  const hours = Array.from({ length: hourCount }, (_, index) =>
    CALENDAR_START_HOUR + index,
  );
  const hourLines = Array.from({ length: hourCount + 1 }, (_, index) =>
    CALENDAR_START_HOUR + index,
  );
  const now = dayjs();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const visibleDaysKey = days.map((day) => day.format("YYYY-MM-DD")).join("|");

  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = 7 * CALENDAR_HOUR_HEIGHT;
    }
  }, [visibleDaysKey]);

  return (
    <div ref={viewportRef} className={styles.timeGridViewport}>
      <div
        className={styles.timeGrid}
        style={{ gridTemplateColumns: `4.25rem repeat(${days.length}, minmax(9rem, 1fr))` }}
      >
        <div className={styles.timeGridCorner} />
        {days.map((day) => (
          <div
            key={day.format("YYYY-MM-DD")}
            className={
              day.isSame(now, "day")
                ? `${styles.dayHeader} ${styles.dayHeaderToday}`
                : styles.dayHeader
            }
          >
            <span>{day.format("ddd")}</span>
            <strong>{day.format("D")}</strong>
          </div>
        ))}

        <div className={styles.hourLabels} style={{ height: gridHeight }}>
          {hours.map((hour) => (
            <span
              key={hour}
              style={{ top: (hour - CALENDAR_START_HOUR) * CALENDAR_HOUR_HEIGHT }}
            >
              {String(hour).padStart(2, "0")}:00
            </span>
          ))}
        </div>

        {days.map((day) => {
          const dayEvents = assignEventLanes(eventsForDay(events, day));
          const showCurrentTime =
            day.isSame(now, "day") &&
            now.hour() >= CALENDAR_START_HOUR &&
            now.hour() < CALENDAR_END_HOUR;
          const currentTop =
            ((now.hour() - CALENDAR_START_HOUR) * 60 + now.minute()) /
            60 *
            CALENDAR_HOUR_HEIGHT;

          return (
            <div
              key={day.format("YYYY-MM-DD")}
              className={styles.dayColumn}
              style={{ height: gridHeight }}
            >
              {hourLines.map((hour) => (
                <span
                  key={hour}
                  className={styles.hourLine}
                  style={{ top: (hour - CALENDAR_START_HOUR) * CALENDAR_HOUR_HEIGHT }}
                />
              ))}

              {showCurrentTime && (
                <span
                  className={styles.currentTimeLine}
                  aria-label={t("calendar.currentTime")}
                  style={{ top: currentTop }}
                />
              )}

              {dayEvents.map(({ event, lane, laneCount }) => {
                const position = eventPosition(event, day);
                const scheduleId = event.scheduleIds[0] ?? "default";
                const gap = 4;
                const laneWidth = 100 / laneCount;
                const style: EventStyle = {
                  top: position.top,
                  height: position.height,
                  left: `calc(${lane * laneWidth}% + ${gap / 2}px)`,
                  width: `calc(${laneWidth}% - ${gap}px)`,
                  "--event-color": getScheduleColor(scheduleId, schedules),
                };

                return (
                  <button
                    key={`${event.id}-${day.format("YYYY-MM-DD")}`}
                    type="button"
                    className={styles.timeEvent}
                    style={style}
                    aria-label={`${event.title}, ${eventTimeLabel(event)}`}
                    onClick={() => onEventClick(event)}
                  >
                    <strong>{event.title}</strong>
                    <span>{eventTimeLabel(event)}</span>
                    {event.locationDisplay && <small>{event.locationDisplay}</small>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
